import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import {NEAR_BLACK, GRAY_LIGHT} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent} from 'util/Types';

import {Audience} from '../../BenchlingAppDAO';
import AudienceRadioButtons from './AudienceRadioButtons';
import BenchlingAppVersionInput, {BenchlingAppVersionReadOnly} from './BenchlingAppVersionInput';
import {ModalType} from './CreateAppModal';
import {AppOrVersion} from './CreateAppTypes';
import ManifestUpload from './ManifestUpload';
export interface CreateAppFormProps {
  modalType: ModalType;
  name?: string;
  description?: string;
  version: string;
  currentVersions: Array<string>;
  audience: Audience;
  uploadedFile: File | null;
  onChangeFile: (file: File | null, version: string | null) => void;
  onChangeAudience: (audience: Audience) => void;
  onChangeIsValid: (isValid: boolean) => void;
  onChangeVersion: (version: string) => void;
  onChangeName: (name: string) => void;
  onChangeDescription: (description: string) => void;
}

export default function CreateAppForm(props: CreateAppFormProps): JSX.Element {
  const appOrVersion =
    props.modalType === ModalType.APP_FROM_MANIFEST || props.modalType === ModalType.APP_FROM_SCRATCH
      ? AppOrVersion.APP
      : AppOrVersion.VERSION;

  const fromManifest =
    props.modalType === ModalType.APP_FROM_MANIFEST || props.modalType === ModalType.VERSION_FROM_MANIFEST;

  const [fileIsValid, setFileIsValid] = useState<boolean>(false);
  const [versionIsValid, setVersionIsValid] = useState<boolean>(true);

  const DESCRIPTION_PLACEHOLDER =
    'Describe what your app does. Anyone with access to this app will see this description in their app workspace.';

  // TODO - sort by largest
  const defaultStarterVersion = props.currentVersions[0];

  const aboutStrings: Record<ModalType, string> = {
    [ModalType.APP_FROM_SCRATCH]: 'Create an app',
    [ModalType.APP_FROM_MANIFEST]:
      'Create a credentialed app in this workspace and use a ' +
      "manifest file to add your app's basic settings and custom configurations.",
    [ModalType.VERSION_FROM_DRAFT]: `Start a new draft based on Version ${defaultStarterVersion}`,
    [ModalType.VERSION_FROM_MANIFEST]: 'Upload an existing manifest file to create your draft.',
  };

  const {name, onChangeIsValid} = props;

  useEffect(() => {
    const nameIsValid = fromManifest || Boolean(name ?? false);
    const fileIsNeededAndValid = !fromManifest || fileIsValid;
    onChangeIsValid(versionIsValid && nameIsValid && fileIsNeededAndValid);
  }, [name, versionIsValid, fileIsValid, fromManifest, onChangeIsValid]);

  function onChangeFile(file: File | null, fileVersion: string | null): void {
    setFileIsValid(file != null && fileVersion != null);
    props.onChangeFile(file, fileVersion);
  }

  return (
    <Col lg={8} className={css(styles.rightCol)}>
      <p>{aboutStrings[props.modalType]}</p>
      {!fromManifest && (
        <>
          <div className={css(styles.formInput)}>
            <TextField
              label="Name"
              value={props.name ?? undefined}
              onChange={(e: InputChangeEvent) => {
                props.onChangeName(e.target.value);
              }}
              placeholder="Name your app"
              dataTestAttrs={{component: 'TextField', element: 'benchlingAppName'}}
            />
          </div>
          <div className={css(styles.formInput)}>
            <InputWrapper
              id="benchlingAppDescription"
              label="Description"
              labelStyle={FieldLabel.STYLES.LABEL}
            >
              <textarea
                name="description"
                value={props.description ?? undefined}
                onChange={(e) => {
                  props.onChangeDescription(e.target.value);
                }}
                rows={3}
                placeholder={DESCRIPTION_PLACEHOLDER}
                {...getTestProps({component: 'textarea', element: 'benchlingAppDescription'})}
              />
            </InputWrapper>
          </div>
        </>
      )}
      {fromManifest && (
        <ManifestUpload
          currentVersions={props.currentVersions}
          uploadedFile={props.uploadedFile}
          onChangeFile={onChangeFile}
        />
      )}
      {appOrVersion === AppOrVersion.VERSION &&
        (fromManifest ? (
          <BenchlingAppVersionReadOnly
            versionString={props.uploadedFile && fileIsValid ? props.version : null}
          />
        ) : (
          <>
            <div className={css(styles.aboutVersion, styles.formInput)}>
              Choose a version increment for your new published version.
            </div>
            <BenchlingAppVersionInput
              currentVersions={props.currentVersions}
              versionString={props.version}
              onValidation={setVersionIsValid}
              onChange={props.onChangeVersion}
            />
          </>
        ))}
      {appOrVersion === AppOrVersion.APP && (
        <div className={css(styles.formInput)}>
          <AudienceRadioButtons state={props.audience} onChangeState={props.onChangeAudience} />
        </div>
      )}
    </Col>
  );
}

const styles = StyleSheet.create({
  aboutVersion: {
    color: NEAR_BLACK,
  },
  formInput: {
    marginBottom: 15,
  },
  rightCol: {
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    borderColor: GRAY_LIGHT,
    paddingTop: 16,
    paddingBottom: 16,
  },
});
